import { authedApiFetch } from '../../../utils/Api';

/**
 * Service for handling email sync configuration API calls
 */
const emailSyncConfigService = {
	/**
	 * Update tenant attributes
	 * @param {string} tenantId - The tenant ID
	 * @param {object} keyValues - Tenant key value pair, ex: { email_body_sync: true }
	 * @returns {Promise<Object>} - Updated tenant data
	 */
	updateTenant: async (tenantId, keyValue) => {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}`,
			method: 'PATCH',
			payload: {
				tenant: keyValue,
			},
		});
		return response;
	},

	/**
	 * Get tenant
	 * @param {string} tenantId - The tenant ID
	 * @returns {Promise<Object>} - Tenant data
	 */
	getTenant: async (tenantId) => {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}`,
			method: 'GET',
		});

		return response;
	},

	/**
	 * Get supported email sync time zones
	 * @returns {Promise<Object>} - Default and supported email sync time zones
	 */
	getEmailSyncTimeZones: async () => {
		const response = await authedApiFetch({
			endpoint: '/tenants/email_sync_time_zones',
			method: 'GET',
		});

		return response;
	},

	/**
	 * Update tenant-level email sync configuration settings
	 * @param {string} tenantId - The tenant ID
	 * @param {object} newState - Updated email sync configuration
	 * @returns {Promise<Object>} - Updated email sync configuration
	 */
	updateTenantEmailSyncConfig: async (tenantId, newState) => {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/email_sync_configuration_settings`,
			method: 'POST',
			payload: {
				tenant_level: true,
				new_state: newState,
			},
		});

		return response;
	},

	/**
	 * Get email rules
	 * @param {boolean} tenantLevel - Whether to get tenant level rules
	 * @param {string} tenantId - The tenant ID
	 * @param {boolean} inclusionEnabled - Whether to get inclusion or exclusion rules
	 * @returns {Promise<Object>} - Email rules
	 */
	getEmailRules: async (tenantLevel, tenantId, inclusionEnabled) => {
		const response = await authedApiFetch({
			endpoint: tenantLevel
				? `/tenants/${tenantId}/email_rules`
				: '/email_rules',
			method: 'GET',
			params: {
				rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
				tenant_level: tenantLevel,
			},
		});
		return response;
	},

	/**
	 * Create new email rule
	 * @param {boolean} tenantLevel - Whether to create tenant or user level rule
	 * @param {string} tenantId - The tenant ID
	 * @param {boolean} inclusionEnabled - Whether to create inclusion or exclusion rule
	 * @param {string} criteriaType - The criteria type: domain, sender_type, specific_email
	 * @param {string} value - The value
	 * @returns {Promise<Object>} - The created email rule
	 */
	createEmailRule: async (
		tenantLevel,
		tenantId,
		inclusionEnabled,
		criteriaType,
		value
	) => {
		const endpoint = tenantLevel
			? `/tenants/${tenantId}/email_rules`
			: '/email_rules';

		const response = await authedApiFetch({
			endpoint,
			method: 'POST',
			payload: {
				email_rule: {
					rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
					criteria_type: criteriaType,
					value: value,
				},
			},
		});
		return response;
	},

	/**
	 * Delete email rule
	 * @param {boolean} tenantLevel - Whether to delete tenant or user level rule
	 * @param {string} tenantId - The tenant ID
	 * @param {string} ruleId - The rule ID
	 * @returns {Promise<Object>} - The deleted email rule
	 */
	deleteEmailRule: async (tenantLevel, tenantId, ruleId) => {
		const endpoint = tenantLevel
			? `/tenants/${tenantId}/email_rules/${ruleId}`
			: `/email_rules/${ruleId}`;

		const response = await authedApiFetch({
			endpoint,
			method: 'DELETE',
		});

		return response;
	},
};

export default emailSyncConfigService;
